import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Alert,
  Skeleton,
  Container
} from '@mui/material';

// Import components
import SystemStatusIndicator from './SystemStatusIndicator';
import PerformanceMetricsCard from './PerformanceMetricsCard';
import AlertFeed from './AlertFeed';

// Import API hooks
import { useSystemStatus, usePerformanceMetrics, useAlerts } from '../hooks/useApi';

// Import types
// Types imported but not used in this component

const Dashboard: React.FC = () => {
  // Use real API data instead of mock data
  const { data: systemStatus, loading: statusLoading, error: statusError, refresh: refreshStatus } = useSystemStatus();
  const { data: performanceMetrics, loading: metricsLoading, error: metricsError } = usePerformanceMetrics();
  const { data: alerts, loading: alertsLoading, error: alertsError } = useAlerts();

  // Determine overall loading state
  const loading = statusLoading || metricsLoading || alertsLoading;
  
  // Collect all errors
  const error = statusError || metricsError || alertsError;
  
  const [localError, setLocalError] = useState<string | null>(null);

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Skeleton variant="rectangular" height={400} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Skeleton variant="rectangular" height={400} />
          </Grid>
        </Grid>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 3 }}>
      {/* Error Alert */}
      {(error || localError) && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setLocalError(null)}>
          {error || localError}
        </Alert>
      )}

      {/* Main Dashboard Grid */}
      <Grid container spacing={3}>
        {/* System Status and Performance Metrics */}
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">System Status</Typography>
                <SystemStatusIndicator 
                  status={systemStatus || { status: 'unknown', lastUpdate: new Date().toISOString(), uptime: 0, message: 'Loading...' }} 
                  loading={statusLoading}
                  onRefresh={refreshStatus}
                />
              </Box>
              <Typography variant="body2" color="text.secondary" mb={2}>
                {systemStatus?.message || 'All systems operational'}
              </Typography>
            </CardContent>
          </Card>

          <PerformanceMetricsCard 
            metrics={performanceMetrics || {
              executionTime: { current: 0, average: 0, trend: 'stable' },
              successRate: { current: 0, average: 0, trend: 'stable' },
              totalAlerts: { today: 0, thisWeek: 0, thisMonth: 0 },
              systemHealth: { cpu: 0, memory: 0, database: 'unknown' },
              lowConfidenceCount: 0,
              failedExecutions: 0
            }} 
            loading={metricsLoading}
          />
        </Grid>

        {/* Alert Feed */}
        <Grid item xs={12} md={4}>
          <AlertFeed 
            alerts={alerts || []} 
            loading={alertsLoading}
          />
        </Grid>
      </Grid>
    </Container>
  );
};

export default Dashboard;